import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCadProject,
  getCadUploadUrl,
  parseCadProject,
  putCadFileToS3,
} from '../../services/cadUploadService';
import {
  CAD_UPLOAD_DEFAULT_CONTENT_TYPE,
  CAD_UPLOAD_STAGE_PARSE,
  CAD_UPLOAD_STAGE_PREPARE,
  CAD_UPLOAD_STAGE_UPLOAD,
  cadUploadMaxBytes,
  suggestCadListName,
  validateCadUploadFile,
} from '../utils/cadUpload';

/**
 * Alta de una lista a partir de un plano CAD.
 * Pasos: `pick` → `preview` (+ `naming`), con `uploading` / `creating`
 * como estados de trabajo. No pide empresa: el dueño es el JWT y
 * `company_id` queda `null`.
 *
 * Reglas del contrato que este hook garantiza:
 * - El PUT a S3 manda el `Content-Type` exacto que devolvió el GET.
 * - Al confirmar viaja solo `job_id` + `name`: nunca el GeoJSON del preview.
 *   Si el usuario cancela, no se llama a `/createcadproject`.
 *
 * @param {{ onCreated?: (project: object) => void }} [options]
 * @see docs/maps/cad-upload.md
 */
export function useCadUpload({ onCreated } = {}) {
  const [step, setStep] = useState('pick');

  const [file, setFile] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState('');

  const [preview, setPreview] = useState(null);
  const [jobId, setJobId] = useState('');
  const [listName, setListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const selectFile = useCallback((nextFile) => {
    setError('');
    const validation = validateCadUploadFile(nextFile, {
      maxBytes: cadUploadMaxBytes(requirements),
    });
    if (!validation.valid) {
      setFile(null);
      setError(validation.message);
      return false;
    }
    setFile(nextFile);
    return true;
  }, [requirements]);

  const clearFile = useCallback(() => {
    setFile(null);
    setError('');
  }, []);

  /**
   * URL prefirmada → PUT a S3 → parse. Sin escribir nada en la base:
   * el preview vive en S3 hasta que el usuario confirme.
   *
   * @param {{ layer?: string, file?: File|null }} [options]
   *   `file` opcional: al elegir el plano el state aún no actualizó;
   *   pasar el File evita un click extra en Subir.
   */
  const uploadAndParse = useCallback(async ({ layer, file: fileArg } = {}) => {
    const activeFile = fileArg || file;
    if (!activeFile || uploading) return false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setUploading(true);
    setError('');
    try {
      setUploadStage(CAD_UPLOAD_STAGE_PREPARE);
      const urlResult = await getCadUploadUrl(
        { filename: activeFile.name },
        { signal: controller.signal },
      );
      if (!mountedRef.current || controller.signal.aborted) return false;
      if (!urlResult.ok || !urlResult.upload?.presignedUrl) {
        setError(urlResult.message || 'No se pudo preparar la carga del plano.');
        return false;
      }

      const upload = urlResult.upload;
      if (upload.requirements) setRequirements(upload.requirements);

      setUploadStage(CAD_UPLOAD_STAGE_UPLOAD);
      const putResult = await putCadFileToS3(
        {
          presignedUrl: upload.presignedUrl,
          file: activeFile,
          // Exactamente el que firmó el API: cambiarlo rompe la firma.
          contentType: upload.contentType || CAD_UPLOAD_DEFAULT_CONTENT_TYPE,
        },
        { signal: controller.signal },
      );
      if (!mountedRef.current || controller.signal.aborted) return false;
      if (!putResult.ok) {
        setError(putResult.message || 'No se pudo subir el archivo.');
        return false;
      }

      setUploadStage(CAD_UPLOAD_STAGE_PARSE);
      const parseResult = await parseCadProject(
        {
          jobId: upload.job_id,
          filename: activeFile.name,
          layer,
        },
        { signal: controller.signal },
      );
      if (!mountedRef.current || controller.signal.aborted) return false;
      if (!parseResult.ok || !parseResult.preview) {
        setError(parseResult.message || 'No se pudo leer el plano.');
        return false;
      }

      setJobId(parseResult.preview.job_id || upload.job_id || '');
      setPreview(parseResult.preview);
      setListName((prev) => prev || suggestCadListName(activeFile.name));
      setStep('preview');
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      console.error('CAD upload: falló la carga del plano', err);
      if (mountedRef.current) setError('No se pudo procesar el plano.');
      return false;
    } finally {
      if (mountedRef.current) {
        setUploading(false);
        setUploadStage('');
      }
    }
  }, [file, uploading]);

  /** Reintento con otra capa del informe: no vuelve a subir el archivo. */
  const reparseWithLayer = useCallback(async (layer) => {
    if (!jobId || uploading) return false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setUploadStage(CAD_UPLOAD_STAGE_PARSE);
    setError('');
    try {
      const result = await parseCadProject(
        { jobId, filename: file?.name, layer },
        { signal: controller.signal },
      );
      if (!mountedRef.current || controller.signal.aborted) return false;
      if (!result.ok || !result.preview) {
        setError(result.message || 'No se pudo leer el plano con esa capa.');
        return false;
      }
      setPreview(result.preview);
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      console.error('CAD upload: falló el reintento de lectura', err);
      if (mountedRef.current) setError('No se pudo leer el plano con esa capa.');
      return false;
    } finally {
      if (mountedRef.current) {
        setUploading(false);
        setUploadStage('');
      }
    }
  }, [file, jobId, uploading]);

  /** Confirmación: solo `job_id` + `name`. El GeoJSON no viaja. */
  const confirmCreate = useCallback(async () => {
    const name = listName.trim();
    if (!jobId || !name || creating) return null;
    setCreating(true);
    setError('');
    try {
      const result = await createCadProject({ jobId, name });
      if (!mountedRef.current) return null;
      if (!result.ok || !result.project) {
        setError(result.message || 'No se pudo crear la lista.');
        return null;
      }
      onCreated?.(result.project);
      return result.project;
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  }, [creating, jobId, listName, onCreated]);

  /** Descartar el preview: se corta todo y **no** se llama al create. */
  const discardPreview = useCallback(() => {
    abortRef.current?.abort();
    setPreview(null);
    setJobId('');
    setFile(null);
    setListName('');
    setError('');
    setStep('pick');
  }, []);

  return {
    step,
    file,
    requirements,
    selectFile,
    clearFile,
    uploading,
    uploadStage,
    uploadAndParse,
    reparseWithLayer,
    preview,
    listName,
    setListName,
    creating,
    confirmCreate,
    discardPreview,
    error,
  };
}
